import React, { useState, useEffect } from "react";
import { Form, Button, Modal, message, Input } from "antd";
import { TwitterPicker } from "react-color";
import {
  addCategoryApi,
  updateIpGalleryFoldersApi,
} from "../../../services/patient.services";
import modalStyles from "./galleryCompactModal.module.css";
import { GALLERY_FOLDER_KEYS } from "../../../helpers/galleryUtils";

const AddLabelModal = ({
  isModalVisible,
  setIsModalVisible,
  refreshLabelsPage,
  ipId,
  existingFolders = [],
  onLocalAdd,
}) => {
  const [form] = Form.useForm();
  const [selectedLabel, setSelectedLabel] = useState("#fff");
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 768px)").matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const onFinish = async (values) => {
    setLoading(true);

    try {
      const newLabel = values?.name.trim();

      const updatedFolders = [...new Set([...existingFolders, newLabel])];

      await updateIpGalleryFoldersApi(ipId, {
        gallery_folders: updatedFolders,
      });

      message.success("Label added to patient gallery");
      onLocalAdd(newLabel);
      setIsModalVisible(false);
      form.resetFields();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to update gallery");
    }

    setLoading(false);
  };

  return (
    <div>
      <Modal
        width={isMobile ? "min(350px, calc(100vw - 24px))" : 350}
        {...(isMobile ? { centered: false } : {})}
        wrapClassName={isMobile ? modalStyles.compactModalWrap : undefined}
        styles={
          isMobile
            ? {
                body: {
                  maxWidth: "100%",
                  overflowX: "hidden",
                  boxSizing: "border-box",
                },
              }
            : undefined
        }
        footer={null}
        title="Labels"
        onCancel={() => setIsModalVisible(false)}
        open={isModalVisible}
      >
        <Form
          name="label"
          requiredMark={false}
          onFinish={onFinish}
          layout="vertical"
          form={form}
          className={isMobile ? modalStyles.compactForm : undefined}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[
              {
                validator: (_, value) => {
                  const restrictedKeys = [
                    GALLERY_FOLDER_KEYS.RECYCLE_BIN,
                    GALLERY_FOLDER_KEYS.DISCHARGE_SUMMARY,
                    GALLERY_FOLDER_KEYS.CONSENT_FORM,
                    GALLERY_FOLDER_KEYS.PATIENT_FILES,
                    "Discharge Summary",
                    "Consent Form",
                    "Recycle Bin",
                    "Patient Files",
                  ];

                  if (!value || value === "") {
                    return Promise.reject("Please enter your label name");
                  }

                  const trimVal = value.trim();

                  if (trimVal === "") {
                    return Promise.reject(
                      "Label cannot be empty or contain only spaces"
                    );
                  }

                  // 🚫 Restrict specific keys
                  if (restrictedKeys.includes(trimVal)) {
                    return Promise.reject("This label name is not allowed");
                  }

                  // 🚫 Check for duplicate label (case-insensitive)
                  if (
                    existingFolders
                      .map((f) => f.toLowerCase())
                      .includes(trimVal.toLowerCase())
                  ) {
                    return Promise.reject("This label already exists");
                  }

                  if (trimVal.length < 3) {
                    return Promise.reject(
                      "Label should be at least 3 characters long"
                    );
                  }

                  if (trimVal.length > 20) {
                    return Promise.reject("Label cannot exceed 20 characters");
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Enter Label Name" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AddLabelModal;
